import { getApiUrl } from "./api";

// In-memory cache for CLIENT_URL
let cachedClientUrl = null;

/**
 * Fetch canonical CLIENT_URL from backend config, or fallback to current origin
 */
export const getShareBaseUrl = async () => {
  if (cachedClientUrl) return cachedClientUrl;

  try {
    const res = await fetch(getApiUrl("/share/config"));
    if (res.ok) {
      const data = await res.json();
      if (data.success && data.clientUrl) {
        cachedClientUrl = data.clientUrl.replace(/\/+$/, "");
        return cachedClientUrl;
      }
    }
  } catch (err) {
    console.warn("⚠️ Could not fetch backend CLIENT_URL, falling back to window.location.origin", err);
  }

  // Fallback to current browser origin
  cachedClientUrl = typeof window !== "undefined" ? window.location.origin : "http://localhost:3000";
  return cachedClientUrl;
};

/**
 * Generate a privacy-friendly persistent visitor ID stored in localStorage
 */
export const getVisitorId = () => {
  if (typeof window === "undefined") return "anon";
  let vid = localStorage.getItem("pk_share_vid");
  if (!vid) {
    vid = "v_" + Math.random().toString(36).substring(2, 11) + "_" + Date.now().toString(36);
    try {
      localStorage.setItem("pk_share_vid", vid);
    } catch (e) {}
  }
  return vid;
};

/**
 * Get or create session ID stored in sessionStorage (cleared on tab close)
 */
export const getSessionId = () => {
  if (typeof window === "undefined") return "sess";
  let sid = sessionStorage.getItem("pk_share_sid");
  if (!sid) {
    sid = "s_" + Math.random().toString(36).substring(2, 11) + "_" + Date.now().toString(36);
    try {
      sessionStorage.setItem("pk_share_sid", sid);
    } catch (e) {}
  }
  return sid;
};

/**
 * Generate default UTM parameters based on selected platform and content item (Project, Blog, or Event)
 */
export const getDefaultUtmParams = (platform, item = {}) => {
  const normPlatform = (platform || "direct").toLowerCase();
  const isEvent =
    item.contentType === "event" ||
    item.type === "event" ||
    (item.path && item.path.toLowerCase().includes("/events"));
  const isBlog =
    !isEvent &&
    (item.contentType === "blog" ||
      item.type === "blog" ||
      (item.path && item.path.toLowerCase().includes("/blog")));
  const prefix = isEvent ? "event" : isBlog ? "blog" : "proj";
  const slug = (item.id || item.slug || (isEvent ? "event" : isBlog ? "blog" : "project"))
    .toLowerCase()
    .replace(/[^a-z0-9_-]/g, "_");
  const campaign = isEvent ? "event_share" : isBlog ? "blog_share" : "portfolio_share";
  const defaultTerm = isEvent ? "portfolio_event" : isBlog ? "portfolio_blog" : "portfolio_project";

  const platformDefaults = {
    linkedin: {
      utm_source: "linkedin",
      utm_medium: "social",
      utm_campaign: campaign,
      utm_id: `${prefix}_${slug}`,
      utm_content: isEvent ? "event_card" : isBlog ? "blog_card" : "share_card",
      utm_term: defaultTerm,
      utm_source_platform: "linkedin_feed",
      utm_creative_format: isEvent ? "event_post" : isBlog ? "article_share" : "post",
      utm_marketing_tactic: "peer_sharing"
    },
    whatsapp: {
      utm_source: "whatsapp",
      utm_medium: "social_message",
      utm_campaign: campaign,
      utm_id: `${prefix}_${slug}`,
      utm_content: "chat_link",
      utm_term: defaultTerm,
      utm_source_platform: "whatsapp_messenger",
      utm_creative_format: "text_message",
      utm_marketing_tactic: "peer_sharing"
    },
    instagram: {
      utm_source: "instagram",
      utm_medium: "social",
      utm_campaign: campaign,
      utm_id: `${prefix}_${slug}`,
      utm_content: "bio_or_story",
      utm_term: defaultTerm,
      utm_source_platform: "instagram_app",
      utm_creative_format: "story_link",
      utm_marketing_tactic: "peer_sharing"
    },
    facebook: {
      utm_source: "facebook",
      utm_medium: "social",
      utm_campaign: campaign,
      utm_id: `${prefix}_${slug}`,
      utm_content: "feed_post",
      utm_term: defaultTerm,
      utm_source_platform: "facebook_web",
      utm_creative_format: "feed_share",
      utm_marketing_tactic: "peer_sharing"
    },
    x: {
      utm_source: "x",
      utm_medium: "social",
      utm_campaign: campaign,
      utm_id: `${prefix}_${slug}`,
      utm_content: "tweet_post",
      utm_term: defaultTerm,
      utm_source_platform: "x_web",
      utm_creative_format: "tweet",
      utm_marketing_tactic: "peer_sharing"
    },
    threads: {
      utm_source: "threads",
      utm_medium: "social",
      utm_campaign: campaign,
      utm_id: `${prefix}_${slug}`,
      utm_content: "thread_post",
      utm_term: defaultTerm,
      utm_source_platform: "threads_app",
      utm_creative_format: "post",
      utm_marketing_tactic: "peer_sharing"
    },
    email: {
      utm_source: "email",
      utm_medium: "email",
      utm_campaign: campaign,
      utm_id: `${prefix}_${slug}`,
      utm_content: isEvent ? "event_invite" : isBlog ? "blog_newsletter" : "direct_email",
      utm_term: defaultTerm,
      utm_source_platform: "email_client",
      utm_creative_format: "email_body",
      utm_marketing_tactic: "direct_outreach"
    },
    copy_link: {
      utm_source: "share_dialog",
      utm_medium: "referral",
      utm_campaign: campaign,
      utm_id: `${prefix}_${slug}`,
      utm_content: "direct_copy",
      utm_term: defaultTerm,
      utm_source_platform: "portfolio_web",
      utm_creative_format: "link_copy",
      utm_marketing_tactic: "direct_sharing"
    }
  };

  return (
    platformDefaults[normPlatform] || {
      utm_source: normPlatform,
      utm_medium: "referral",
      utm_campaign: campaign,
      utm_id: `${prefix}_${slug}`,
      utm_content: "direct_share",
      utm_term: defaultTerm,
      utm_source_platform: "portfolio_web",
      utm_creative_format: "link",
      utm_marketing_tactic: "sharing"
    }
  );
};

/**
 * Build a full canonical URL with UTM parameters safely encoded
 */
export const buildFullShareUrl = (baseUrl, contentPath, utmParams = {}) => {
  const cleanBase = (baseUrl || window.location.origin).replace(/\/+$/, "");
  const cleanPath = (contentPath || window.location.pathname).startsWith("/")
    ? contentPath || window.location.pathname
    : `/${contentPath || window.location.pathname}`;

  try {
    const url = new URL(`${cleanBase}${cleanPath}`);
    const keys = [
      "utm_source",
      "utm_medium",
      "utm_campaign",
      "utm_id",
      "utm_content",
      "utm_term",
      "utm_source_platform",
      "utm_creative_format",
      "utm_marketing_tactic"
    ];

    keys.forEach((key) => {
      const val = utmParams[key];
      if (val && typeof val === "string" && val.trim()) {
        url.searchParams.set(key, val.trim());
      }
    });

    return url.toString();
  } catch (e) {
    // Fallback if URL construction fails
    const query = Object.entries(utmParams)
      .filter(([_, v]) => Boolean(v && String(v).trim()))
      .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v).trim())}`)
      .join("&");
    return `${cleanBase}${cleanPath}${query ? `?${query}` : ""}`;
  }
};

/**
 * Record a share action event via backend API
 */
export const trackShareActionApi = async ({
  contentType = "project",
  projectId,
  projectTitle,
  projectPath,
  blogId,
  blogTitle,
  blogPath,
  eventId,
  eventTitle,
  eventPath,
  platform,
  actionType,
  utmParams
}) => {
  try {
    const visitorId = getVisitorId();
    const sessionId = getSessionId();

    const targetId = eventId || blogId || projectId;
    const targetTitle = eventTitle || blogTitle || projectTitle;
    const targetPath = eventPath || blogPath || projectPath;

    const response = await fetch(getApiUrl("/share/track-action"), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        contentType,
        projectId: targetId,
        projectTitle: targetTitle,
        projectPath: targetPath,
        blogId: contentType === "blog" ? targetId : undefined,
        blogTitle: contentType === "blog" ? targetTitle : undefined,
        blogPath: contentType === "blog" ? targetPath : undefined,
        eventId: contentType === "event" ? targetId : undefined,
        eventTitle: contentType === "event" ? targetTitle : undefined,
        eventPath: contentType === "event" ? targetPath : undefined,
        platform,
        actionType,
        utmParams,
        visitorId,
        sessionId,
        metadata: {
          screen: typeof window !== "undefined" ? `${window.screen.width}x${window.screen.height}` : "",
          viewport: typeof window !== "undefined" ? `${window.innerWidth}x${window.innerHeight}` : ""
        }
      })
    });

    return await response.json();
  } catch (error) {
    console.warn("⚠️ Failed to record share action:", error);
    return { success: false, error: error.message };
  }
};

/**
 * Record a tracked landing visit when someone arrives via UTM link
 */
export const trackLandingVisitApi = async ({
  contentType = "project",
  projectId,
  projectTitle,
  projectPath,
  blogId,
  blogTitle,
  blogPath,
  eventId,
  eventTitle,
  eventPath
}) => {
  try {
    if (typeof window === "undefined") return;

    const params = new URLSearchParams(window.location.search);
    const utmParams = {
      utm_source: params.get("utm_source") || "",
      utm_medium: params.get("utm_medium") || "",
      utm_campaign: params.get("utm_campaign") || "",
      utm_id: params.get("utm_id") || "",
      utm_content: params.get("utm_content") || "",
      utm_term: params.get("utm_term") || "",
      utm_source_platform: params.get("utm_source_platform") || "",
      utm_creative_format: params.get("utm_creative_format") || "",
      utm_marketing_tactic: params.get("utm_marketing_tactic") || ""
    };

    // If no UTM parameters exist, do not track as campaign landing
    if (!utmParams.utm_source && !utmParams.utm_campaign && !utmParams.utm_id) {
      return;
    }

    const targetId = eventId || (contentType === "blog" ? (blogId || projectId) : (projectId || blogId));
    const targetTitle = eventTitle || (contentType === "blog" ? (blogTitle || projectTitle) : (projectTitle || blogTitle));
    const targetPath = eventPath || (contentType === "blog" ? (blogPath || projectPath) : (projectPath || blogPath)) || window.location.pathname;

    // Deduplication via sessionStorage to prevent double-firing on React re-renders or refreshes
    const dedupeKey = `pk_utm_visit_${contentType}_${targetId}_${window.location.search}`;
    if (sessionStorage.getItem(dedupeKey)) {
      return; // Already tracked for this browser tab session
    }
    sessionStorage.setItem(dedupeKey, "tracked");

    const visitorId = getVisitorId();
    const sessionId = getSessionId();

    await fetch(getApiUrl("/share/track-visit"), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        contentType,
        projectId: targetId,
        projectTitle: targetTitle,
        projectPath: targetPath,
        blogId: contentType === "blog" ? targetId : undefined,
        blogTitle: contentType === "blog" ? targetTitle : undefined,
        blogPath: contentType === "blog" ? targetPath : undefined,
        eventId: contentType === "event" ? targetId : undefined,
        eventTitle: contentType === "event" ? targetTitle : undefined,
        eventPath: contentType === "event" ? targetPath : undefined,
        utmParams,
        visitorId,
        sessionId,
        referrer: document.referrer || ""
      })
    });
  } catch (error) {
    console.warn("⚠️ Failed to record landing visit:", error);
  }
};

/**
 * Build third-party platform share URLs
 */
export const getPlatformShareIntent = (platform, finalUrl, shareMessage, contentTitle, isBlog = false, isEvent = false) => {
  const encodedUrl = encodeURIComponent(finalUrl);
  const defaultLabel = isEvent ? "Portfolio Event" : isBlog ? "Portfolio Blog" : "Portfolio Project";
  const titleText = contentTitle || defaultLabel;
  const messageText =
    shareMessage ||
    (isEvent
      ? `Check out "${titleText}" on Praveen's Portfolio Events!`
      : isBlog
      ? `Check out "${titleText}" on Praveen's Portfolio Blog!`
      : `Check out ${titleText} on Praveen's Portfolio!`);
  const encodedText = encodeURIComponent(messageText);

  switch (platform) {
    case "email":
      return {
        type: "link",
        url: `mailto:?subject=${encodeURIComponent(
          isEvent
            ? `Invitation: ${titleText} by Praveen Kumar B`
            : isBlog
            ? `Check out this blog: ${titleText} by Praveen Kumar B`
            : `Check out ${titleText} by Praveen Kumar B`
        )}&body=${encodeURIComponent(
          `${messageText}\n\n${isEvent ? "View Event & Register" : isBlog ? "Read the full blog" : "Project Link"}: ${finalUrl}`
        )}`
      };
    case "linkedin":
      return {
        type: "window",
        url: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`
      };
    case "whatsapp":
      return {
        type: "window",
        url: `https://api.whatsapp.com/send?text=${encodeURIComponent(`${messageText} ${finalUrl}`)}`
      };
    case "facebook":
      return {
        type: "window",
        url: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`
      };
    case "x":
      return {
        type: "window",
        url: `https://x.com/intent/tweet?text=${encodedText}&url=${encodedUrl}`
      };
    case "threads":
      return {
        type: "window",
        url: `https://www.threads.net/intent/post?text=${encodeURIComponent(`${messageText} ${finalUrl}`)}`
      };
    case "instagram":
      // Instagram doesn't support direct URL sharing through web API
      return {
        type: "copy_notice",
        message: "Instagram does not support web prefilled links. We've copied your tracking link to clipboard so you can paste it in your Instagram Story, Bio, or DM!"
      };
    case "copy_link":
      return {
        type: "copy"
      };
    default:
      return {
        type: "copy"
      };
  }
};
